/** @jsxImportSource @emotion/react */
import { useRef } from 'react'
import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { Box, useTheme, Typography } from '@mui/material'
import { Button } from '@app/components/Atoms/Button/Button'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import { AdaptionLibraryCardItem, AdaptationLibraryHazard } from '../utils/adaptationLibrary.types'
import { COSTS_MAP, HAZARDS_COLOUR_MAP } from '../utils/adaptationLibrary.constants'
import {
  adaptationLibraryCard,
  adaptationLibraryCardLower,
  adaptationLibraryCardUpper,
  adaptationLibraryCardBadge,
  adaptationLibraryCardBadges,
  adaptationLibraryCardViewButton,
  adaptationLibraryTitle,
  adaptionLibraryButton,
} from '../styles/AdaptationLibrary.styles'
import { useLineClamp } from '../utils/useLineClamp'

export const AdaptationLibraryCard = ({ adaptationLibraryItem }: AdaptionLibraryCardItem) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const currencySymbol = useAtomValue(currencySymbolAtom)
  const descriptionBoundsRef = useRef(null)
  const clamp = useLineClamp(descriptionBoundsRef)

  const { intervention_name, hazards_addressed, description, cost, timeframe } =
    adaptationLibraryItem

  function handleSetOpenDrawer() {
    navigate(adaptationLibraryItem.id)
  }

  return (
    <Box css={adaptationLibraryCard()} key={intervention_name}>
      <Box css={adaptationLibraryCardUpper({ theme })}>
        <Typography css={{ ...adaptationLibraryTitle() }}>{intervention_name}</Typography>
        <Box css={adaptationLibraryCardBadges()}>
          {hazards_addressed.map((hazard: AdaptationLibraryHazard) => {
            return (
              <Box
                key={hazard}
                css={adaptationLibraryCardBadge({
                  theme,
                  color: HAZARDS_COLOUR_MAP[hazard].color || '#005C7A',
                  background: HAZARDS_COLOUR_MAP[hazard].background || '#BAFAFF',
                })}
              >
                {hazard}
              </Box>
            )
          })}
        </Box>
        <Box
          css={{
            WebkitLineClamp: `${clamp}`,
            height: `calc(13px * 1.4 * ${clamp})`,
            display: 'block',
            //@ts-expect-error ignore the next line, its for browser compatibility
            display: '-webkit-box',
            margin: '0 auto',
            fontSize: '13px',
            lineHeight: '18px',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          ref={descriptionBoundsRef}
        >
          {description === 'null' ? 'No description available' : description}
        </Box>
      </Box>
      <Box css={adaptationLibraryCardLower({ theme })}>
        <Box>
          <Typography css={{ fontWeight: 'bold' }}>
            Cost:{' '}
            <span style={{ fontWeight: 'normal' }}>
              {COSTS_MAP[cost as keyof typeof COSTS_MAP].replaceAll('$', currencySymbol || '$')}
            </span>
          </Typography>
          <p style={{ fontWeight: 'bold' }}>
            Timeline: <span style={{ fontWeight: 'normal' }}>{timeframe}</span>
          </p>
        </Box>
        <Button
          onClick={handleSetOpenDrawer}
          css={{ ...adaptationLibraryCardViewButton(), ...adaptionLibraryButton() }}
          variant="contained"
          sx={{ background: '#0c98ff', color: 'white' }}
        >
          View Details
        </Button>
      </Box>
    </Box>
  )
}
